/**
 * Dashboard — agency home page, rebuilt to scale past 40 clients.
 *
 * Layout: portfolio KPI strip → industry news (slim) + top movers (two cols)
 *   → attention queue (left col, 2/3) + opportunity-of-week (right col, 1/3)
 *   → reporting queue (slim) → GA/GSC reconnect banner.
 *
 * The old "card per client" grid is gone — it doesn't scale and lives on the
 * dedicated /clients page where it belongs. The dashboard's new job is
 * triage, not inventory.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Plus, ArrowRight } from "lucide-react";
import { useAuth } from "../lib/auth";

import GoogleConnectBanner from "../components/GoogleConnectBanner";
import AIInsightsFeed from "../components/AIInsightsFeed";
import PortfolioKpiStrip from "../components/dashboard/PortfolioKpiStrip";
import TopMoversCard from "../components/dashboard/TopMoversCard";
import IndustryNewsCard from "../components/dashboard/IndustryNewsCard";
import ReportingQueueCard from "../components/dashboard/ReportingQueueCard";

// Time-of-day greeting. Uses the browser's local hour so it tracks whoever
// is logged in wherever they are, no backend trip needed.
function greeting() {
  const h = new Date().getHours();
  if (h >= 5 && h < 12) return "Good morning";
  if (h >= 12 && h < 18) return "Good afternoon";
  return "Good evening";
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const firstName =
    (user?.name && user.name.split(/\s+/)[0]) ||
    (user?.email && user.email.split("@")[0]) ||
    "there";

  // Scope mirrors the AIInsightsFeed component — "mine" filters by the
  // logged-in user, "all" shows the agency-wide view. Admin with zero
  // assignments silently flips to "all" on the server.
  const [scope, setScope] = useState("mine");
  const [kpis, setKpis] = useState(null);
  const [movers, setMovers] = useState(null);
  const [news, setNews] = useState(null);
  const [reporting, setReporting] = useState(null);
  const [loading, setLoading] = useState({ kpis: true, movers: true, news: true, reporting: true });
  const [newsRefreshing, setNewsRefreshing] = useState(false);

  const loadKpis = useCallback(async () => {
    setLoading((p) => ({ ...p, kpis: true }));
    try { const { data } = await api.get(`/dashboard/portfolio-kpis?scope=${scope}`); setKpis(data); }
    finally { setLoading((p) => ({ ...p, kpis: false })); }
  }, [scope]);

  const loadMovers = useCallback(async () => {
    setLoading((p) => ({ ...p, movers: true }));
    try { const { data } = await api.get(`/dashboard/top-movers?scope=${scope}`); setMovers(data); }
    finally { setLoading((p) => ({ ...p, movers: false })); }
  }, [scope]);

  const loadNews = useCallback(async () => {
    setLoading((p) => ({ ...p, news: true }));
    try { const { data } = await api.get(`/dashboard/industry-news`); setNews(data); }
    finally { setLoading((p) => ({ ...p, news: false })); }
  }, []);

  const loadReporting = useCallback(async () => {
    setLoading((p) => ({ ...p, reporting: true }));
    try { const { data } = await api.get(`/dashboard/reporting-queue?scope=${scope}`); setReporting(data); }
    finally { setLoading((p) => ({ ...p, reporting: false })); }
  }, [scope]);

  useEffect(() => { loadKpis(); loadMovers(); loadReporting(); }, [loadKpis, loadMovers, loadReporting]);
  useEffect(() => { loadNews(); }, [loadNews]);

  const refreshNews = async () => {
    setNewsRefreshing(true);
    try { const { data } = await api.get(`/dashboard/industry-news`); setNews(data); }
    finally { setNewsRefreshing(false); }
  };

  return (
    <div className="space-y-6" data-testid="dashboard-root">
      {/* Header */}
      <div className="flex items-end justify-between pt-2 gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.16em] text-[#a0aec0] font-semibold">
            Pages / Dashboard
          </div>
          <h1 className="font-display text-3xl mt-1 text-[#1a202c]" data-testid="dashboard-greeting">
            {greeting()}, <span className="text-[#2c7a7b]">{firstName}</span>.
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {/* Scope toggle — mirrors the AIInsightsFeed one so the whole page
              flips in sync. Only shown when "mine" would actually filter. */}
          <div className="inline-flex rounded-full border border-[#e2e8f0] bg-white p-0.5" data-testid="scope-toggle">
            {["mine", "all"].map((s) => (
              <button
                key={s}
                onClick={() => setScope(s)}
                data-testid={`scope-${s}`}
                className={`text-[11px] font-semibold px-3 py-1 rounded-full transition-all ${
                  scope === s
                    ? "bg-[#4fd1c5] text-white shadow-sm"
                    : "text-[#718096] hover:text-[#1a202c]"
                }`}
              >
                {s === "mine" ? "Mine" : "Agency"}
              </button>
            ))}
          </div>
          <button
            onClick={() => navigate("/clients")}
            data-testid="dashboard-view-all-clients"
            className="btn-ghost inline-flex items-center gap-2"
          >
            View all clients <ArrowRight className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => navigate("/clients")}
            data-testid="dashboard-add-client"
            className="btn-primary inline-flex items-center gap-2"
          >
            <Plus className="h-3.5 w-3.5" /> New client
          </button>
        </div>
      </div>

      {/* Portfolio KPI strip — 5 numbers with WoW deltas */}
      <PortfolioKpiStrip data={kpis} loading={loading.kpis} />

      {/* Top movers side-by-side */}
      <TopMoversCard data={movers} loading={loading.movers} />

      {/* Industry news — full-width, grouped by source, 3 articles each */}
      <IndustryNewsCard data={news} loading={loading.news} onRefresh={refreshNews} refreshing={newsRefreshing} />

      {/* AI-generated per-client "what to action this month" grid */}
      <AIInsightsFeed />

      {/* Reporting queue — slim bottom strip */}
      <ReportingQueueCard data={reporting} loading={loading.reporting} onReload={loadReporting} />

      {/* Google Analytics / Search Console reconnect banner */}
      <GoogleConnectBanner />
    </div>
  );
}
